import React from 'react';
import { Box, Grow, List } from '@mui/material';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import MenuItemRow from './MenuItemRow';
import { MENU_TONE, MENU_WIDTH } from './menuTheme';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import axios from 'src/axios';
import { APICONSTANTS, PEDIGREE } from 'src/components/Constants';
import { SnackContext } from 'src/store/ContextStore';
import { omitSpouseRelationship } from '../pedigree/edgeIntegrity';
// One definition of "prune the twin relations to these pids and re-derive what's
// left" — this file used to be its only home, and TGCLOUD-1643 needs the same
// operation for a deliberate un-twin (twinLinkage.js).
import { clearTwinData, pruneAndRederiveTwins } from '../pedigree/twinLinkage';
import { EDGE_KINDS } from './edgeKinds';
import { usePruneOrderForStructuralEdit } from '../structuralEditContext';
import { useAutoTranslation } from 'src/hooks/useTranslate';

function parseParentIds(from) {
  if (typeof from === 'string' && from.startsWith('union-')) {
    const parts = from.split('-');
    return [parseInt(parts[1], 10), parseInt(parts[2], 10)];
  }
  return [from];
}

function dedupe(members) {
  return Array.from(new Map(members.map((m) => [m.pid, m])).values());
}

function EdgeContextMenu({
  edge,
  user,
  memberRecords,
  setMemberRecords,
  setPedigreeState,
  onClose
}) {
  const t = useAutoTranslation();

  const { setSnack } = React.useContext(SnackContext);
  const pruneOrderForStructuralEdit = usePruneOrderForStructuralEdit();

  if (!edge || !EDGE_KINDS.includes(edge.kind)) return null;

  const isRealPid = (pid) => memberRecords.some((m) => m.pid === pid);
  if (
    edge.kind === 'spouse-line' &&
    (!isRealPid(edge.leftPid) || !isRealPid(edge.rightPid))
  ) {
    return null;
  }

  // A remove-relation is an edit and goes through the full save like every
  // other one (TGCLOUD-1813). The set-relation POST below is a per-pid merge
  // into the STORED topology, and on a family with none stored it deliberately
  // writes nothing (`configWritten: false`) — before this, the merged records
  // reached setMemberRecords but the save status stayed SAVED, so
  // planSaveAttempt answered "nothing pending" and the removal was persisted
  // nowhere. Same pattern as SetRelation's markUnsaved.
  const markUnsaved = () => {
    setPedigreeState?.((s) => ({
      ...s,
      save: PEDIGREE.saveStatus.IN_PROGRESS
    }));
  };

  const save = async (affected) => {
    try {
      await axios.post(
        APICONSTANTS.updatePedigreeRelaiton.replace(':user_id', user._id),
        { config: affected.map((m) => ({ data: m })) }
      );
      const byPid = new Map(affected.map((m) => [m.pid, m]));
      // IN_PROGRESS before the records change, so the scheduler the change
      // arms already sees a pending edit when it fires.
      markUnsaved();
      setMemberRecords((prev) => prev.map((m) => byPid.get(m.pid) || m));
      // Every removal here can detach people from the proband's family or move
      // them to another row, so their stored ranks go in the SAME update
      // (TGCLOUD-1786, structuralEditContext.js). The records `affected` was
      // computed from are the "before".
      pruneOrderForStructuralEdit(
        memberRecords,
        memberRecords.map((m) => byPid.get(m.pid) || m)
      );
      onClose();
    } catch (e) {
      console.error('[EdgeContextMenu] remove relation failed:', e);
      setSnack({
        status: true,
        msg: t('Failed to remove relationship. Please try again.'),
        severity: 'error'
      });
      onClose();
    }
  };

  const removeChild = () => {
    const childPid = edge.to;
    const parentPids = parseParentIds(edge.from);
    const child = memberRecords.find((m) => m.pid === childPid);
    if (!child) return;

    const origFatherId = child.fatherId ?? null;
    const origMotherId = child.motherId ?? null;
    const hadParent = origFatherId !== null || origMotherId !== null;

    const updatedChild = clearTwinData({
      ...child,
      fatherId: parentPids.includes(child.fatherId) ? null : child.fatherId,
      motherId: parentPids.includes(child.motherId) ? null : child.motherId
    });

    const formerSiblings = hadParent
      ? memberRecords.filter(
          (m) =>
            m.pid !== childPid &&
            (m.fatherId ?? null) === origFatherId &&
            (m.motherId ?? null) === origMotherId
        )
      : [];
    const formerSiblingPids = formerSiblings.map((s) => s.pid);

    updatedChild.siblingIds = (updatedChild.siblingIds || []).filter(
      (id) => !formerSiblingPids.includes(id)
    );

    const affected = [updatedChild];
    const formerSiblingsSibPruned = formerSiblings.map((s) => ({
      ...s,
      siblingIds: (s.siblingIds || []).filter((id) => id !== childPid)
    }));
    pruneAndRederiveTwins(formerSiblingsSibPruned, [childPid]).forEach((s) =>
      affected.push(s)
    );
    parentPids.forEach((ppid) => {
      const parent = memberRecords.find((m) => m.pid === ppid);
      if (parent)
        affected.push({
          ...parent,
          childrenIds: (parent.childrenIds || []).filter(
            (id) => id !== childPid
          )
        });
    });

    save(dedupe(affected));
  };

  const removeChildren = () => {
    const childPids = edge.childrenPids || [];
    const parentPids = parseParentIds(edge.from);
    const childPidSet = new Set(childPids);
    const affected = [];
    const externalsByPid = new Map();

    childPids.forEach((childPid) => {
      const child = memberRecords.find((m) => m.pid === childPid);
      if (!child) return;

      const origFatherId = child.fatherId ?? null;
      const origMotherId = child.motherId ?? null;
      const hadParent = origFatherId !== null || origMotherId !== null;

      const updatedChild = clearTwinData({
        ...child,
        fatherId: parentPids.includes(child.fatherId) ? null : child.fatherId,
        motherId: parentPids.includes(child.motherId) ? null : child.motherId
      });

      const externalFormerSiblings = hadParent
        ? memberRecords.filter(
            (m) =>
              !childPidSet.has(m.pid) &&
              (m.fatherId ?? null) === origFatherId &&
              (m.motherId ?? null) === origMotherId
          )
        : [];

      updatedChild.siblingIds = (updatedChild.siblingIds || []).filter(
        (id) =>
          !childPidSet.has(id) &&
          !externalFormerSiblings.some((s) => s.pid === id)
      );

      affected.push(updatedChild);
      externalFormerSiblings.forEach((s) => {
        const cur = externalsByPid.get(s.pid) || s;
        externalsByPid.set(s.pid, {
          ...cur,
          siblingIds: (cur.siblingIds || []).filter((id) => id !== childPid)
        });
      });
    });

    pruneAndRederiveTwins(
      Array.from(externalsByPid.values()),
      childPids
    ).forEach((s) => affected.push(s));

    parentPids.forEach((ppid) => {
      const parent = memberRecords.find((m) => m.pid === ppid);
      if (parent)
        affected.push({
          ...parent,
          childrenIds: (parent.childrenIds || []).filter(
            (id) => !childPidSet.has(id)
          )
        });
    });

    save(dedupe(affected));
  };

  const removeSpouse = (type) => {
    const removedPid =
      type === 'remove-spouse-left' ? edge.leftPid : edge.rightPid;
    const retainedPid =
      type === 'remove-spouse-left' ? edge.rightPid : edge.leftPid;

    const removedSpouse = memberRecords.find((m) => m.pid === removedPid);
    const retainedSpouse = memberRecords.find((m) => m.pid === retainedPid);
    if (!removedSpouse || !retainedSpouse) return;

    const sharedChildren = memberRecords.filter((m) => {
      const parents = [m.fatherId, m.motherId].filter((p) => p != null);
      return parents.includes(edge.leftPid) && parents.includes(edge.rightPid);
    });
    const sharedChildPidSet = new Set(sharedChildren.map((c) => c.pid));

    // Drop the now-severed partner from each spouse's per-couple
    // spouseRelationships map so it doesn't leave an orphaned key (the matching
    // spouseId is gone). Returns {} when there is nothing to strip, so the
    // field is only overridden when it actually changes.
    const stripSpouseRel = (member, partnerPid) => {
      const next = omitSpouseRelationship(
        member.spouseRelationships,
        partnerPid
      );
      return next === member.spouseRelationships
        ? {}
        : { spouseRelationships: next };
    };

    const affected = [
      {
        ...removedSpouse,
        spouseIds: (removedSpouse.spouseIds || []).filter(
          (id) => id !== retainedPid
        ),
        childrenIds: (removedSpouse.childrenIds || []).filter(
          (id) => !sharedChildPidSet.has(id)
        ),
        ...stripSpouseRel(removedSpouse, retainedPid)
      },
      {
        ...retainedSpouse,
        spouseIds: (retainedSpouse.spouseIds || []).filter(
          (id) => id !== removedPid
        ),
        ...stripSpouseRel(retainedSpouse, removedPid)
      },
      ...sharedChildren.map((child) => ({
        ...child,
        fatherId: child.fatherId === removedPid ? null : child.fatherId,
        motherId: child.motherId === removedPid ? null : child.motherId
      }))
    ];

    save(affected);
  };

  return (
    <ClickAwayListener onClickAway={onClose}>
      <Grow in={!!edge} style={{ transformOrigin: 'top bottom' }}>
        {/* Same shell and same rows as the node menu. This is a right-click on
            the same canvas, so leaving it on the old 170px/#777 card would have
            been exactly the drift TGCLOUD-1644 set out to remove.
            Neutral rather than red: these sever a relationship, they do not
            delete a person, and red is reserved for Delete Person alone. */}
        <Box
          sx={{
            width: MENU_WIDTH,
            bgcolor: 'background.paper',
            borderRadius: 1.5,
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: 3,
            margin: 1,
            overflow: 'hidden'
          }}
        >
          <List disablePadding>
            {edge.kind === 'child' ? (
              <MenuItemRow
                icon={<LinkOffIcon fontSize="small" />}
                tone={MENU_TONE.neutral}
                label={t('Remove Child')}
                onClick={removeChild}
              />
            ) : edge.kind === 'spouse-line' ? (
              <>
                <MenuItemRow
                  icon={<LinkOffIcon fontSize="small" />}
                  tone={MENU_TONE.neutral}
                  label={t('Remove Spouse on Left')}
                  onClick={() => removeSpouse('remove-spouse-left')}
                />
                <MenuItemRow
                  icon={<LinkOffIcon fontSize="small" />}
                  tone={MENU_TONE.neutral}
                  label={t('Remove Spouse on Right')}
                  onClick={() => removeSpouse('remove-spouse-right')}
                />
              </>
            ) : (
              <MenuItemRow
                icon={<LinkOffIcon fontSize="small" />}
                tone={MENU_TONE.neutral}
                label={t('Remove Children')}
                onClick={removeChildren}
              />
            )}
          </List>
        </Box>
      </Grow>
    </ClickAwayListener>
  );
}

export default EdgeContextMenu;
